'use client'

import { createContext, useContext, useCallback, ReactNode } from 'react'
import { toast } from 'sonner'
import { Toaster } from '@/components/ui/sonner'

type ToastType = 'success' | 'error' | 'info' | 'warning'

interface ToastContextType {
  showToast: (message: string, type?: ToastType, duration?: number) => void
  success: (message: string, duration?: number) => void
  error: (message: string, duration?: number) => void
  info: (message: string, duration?: number) => void
  warning: (message: string, duration?: number) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return context
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const showToast = useCallback((message: string, type: ToastType = 'info', duration = 3000) => {
    const options = { duration }
    switch (type) {
      case 'success':
        toast.success(message, options)
        break
      case 'error':
        toast.error(message, options)
        break
      case 'warning':
        toast.warning(message, options)
        break
      default:
        toast.info(message, options)
    }
  }, [])

  const success = useCallback((message: string, duration = 3000) => {
    toast.success(message, { duration })
  }, [])

  const error = useCallback((message: string, duration = 4000) => {
    toast.error(message, { duration })
  }, [])

  const info = useCallback((message: string, duration = 3000) => {
    toast.info(message, { duration })
  }, [])

  const warning = useCallback((message: string, duration = 3000) => {
    toast.warning(message, { duration })
  }, [])

  return (
    <ToastContext.Provider value={{ showToast, success, error, info, warning }}>
      {children}
      <Toaster position="top-right" richColors closeButton />
    </ToastContext.Provider>
  )
}

// 导出 ToastType 以保持兼容
export type { ToastType }
